import { useCallback, useEffect, useState } from "react";
import { Button, Modal, Input, Form, Pagination, Icon } from "semantic-ui-react";
import { toast } from "../..";
import { tableFilterInit, ITableDto, ITableFilter } from "../../models/table";
import { getTables, getTablesData } from "../../services/tablesService";
import "./SelectionModal.css";
import { TableComponent } from "../FiguresAndTables/Tables/TableComponent";

interface IProps {
    open: boolean;
    onClose: () => void;
    onSelect: (guid: string, tableName: string) => void;
}

export const TableSelectionModal = ({ open, onClose, onSelect }: IProps) => {
    const [tableFilter, setTableFilter] = useState<ITableFilter>(tableFilterInit);
    const [tables, setTables] = useState<ITableDto[]>([]);

    const [page, setPage] = useState(1);
    const [totalImages, setTotalImages] = useState(0);
    const numberOfItemsToFetch = 10;

    const refreshTables = useCallback(async () => {
        try {
            const res = await getTables(tableFilter, page, numberOfItemsToFetch);
            setTables(res.result);
            setTotalImages(res.totalRows);
        } catch (err) {
            toast("Unable to load tables", false, 1000);
        }
    }, [tableFilter, page]);

    useEffect(() => {
        refreshTables();
    }, [refreshTables]);

    useEffect(() => {
        setPage(1);
    }, [tableFilter]);

    return (
        <Modal open={open} onClose={onClose}>
            <Modal.Header>Table selection</Modal.Header>
            <Modal.Content>
                <Input
                    value={tableFilter.name}
                    onChange={(_e, data) => setTableFilter({ ...tableFilter, name: data.value })}
                    icon="search"
                />
                <div className="selection-container">
                    {tables.map(table => {
                        return (
                            <TableSelectDisplay
                                key={table.guid}
                                table={table}
                                onSelectionConfirm={onSelect}
                            />
                        );
                    })}
                </div>
                <div className="selection-modal-pagination-container">
                    <Pagination
                        activePage={page}
                        totalPages={Math.ceil(totalImages / numberOfItemsToFetch)}
                        onPageChange={(_event, data) => setPage(data.activePage as number)}
                    />
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button content="Cancel" onClick={onClose} />
            </Modal.Actions>
        </Modal>
    );
};

interface IFigSelectProps {
    table: ITableDto;
    onSelectionConfirm: (guid: string, tableName: string) => void;
}

const TableSelectDisplay = ({ table, onSelectionConfirm }: IFigSelectProps) => {
    const [displayOpen, setDisplayOpen] = useState(false);
    const [tableChapterName, setTableChapterName] = useState(table.name);
    const [tableData, setTableData] = useState<string[][]>([]);

    const fetchTableData = useCallback(async () => {
        try {
            const res = await getTablesData([table.guid]);
            setTableData(res[table.guid]);
        } catch (err) {
            toast("Unable to load table data", false, 1000);
        }
    }, [table.guid]);

    useEffect(() => {
        if (displayOpen) {
            fetchTableData();
        }
    }, [displayOpen]);

    return (
        <div>
            <div className="figure-table-row-container">
                <div className="figure-table-row" onClick={() => setDisplayOpen(true)}>
                    <Icon name="table" />
                    <p>{table.name}</p>
                </div>
            </div>

            <Modal open={displayOpen} onClose={() => setDisplayOpen(false)}>
                <Modal.Header>{table.name}</Modal.Header>
                <Modal.Content className="image-content-container">
                    <Form>
                        <Form.Field>
                            <label>Table name</label>
                            <Input
                                placeholder="Name used for this table in this chapter.."
                                value={tableChapterName}
                                onChange={e => setTableChapterName(e.target.value)}
                            />
                        </Form.Field>
                    </Form>
                    <span className="table-data-container">
                        <TableComponent tableData={tableData} />
                    </span>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Close" onClick={() => setDisplayOpen(false)} />
                    <Button
                        content="Add"
                        onClick={() => onSelectionConfirm(table.guid, tableChapterName)}
                    />
                </Modal.Actions>
            </Modal>
        </div>
    );
};
